/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { useMemo, useState, useCallback } from 'react'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { connect } from 'react-redux'
import { getPlayerTypesBySportId } from '@sportninja/common/selectors/types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { Link } from 'react-router-dom'
import { Tooltip, Table, Typography, Avatar } from 'antd'
import { getTableStyles } from 'src/styles/tableStyles'
import useWindowSize from 'src/hooks/useWindowSize'
import { InviteAction } from 'src/components/ActionPanel/ActionPanelActions'
import OptionButtonDelete from 'src/components/List/OptionButtons/Delete'
import MoreOptions from 'src/components/List/OptionButtons/More'
import RosterPlayerModal from 'src/features/team/player/RosterPlayerModal'
import FamilyManagementModal from 'src/features/profile/family/FamilyManagementModal'
import QuestionMarkIcon from 'src/components/List/icons/QuestionMarkIcon'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import Icon from 'src/components/Icon'
import { useRosterContext } from '../context/RosterContext'
import { useUIContext } from '../context/UIContext'
import { useScrollToRow } from '../hooks/useScrollToRow'
import SectionTitle from './SectionTitle'

dayjs.extend(utc)

interface PlayerSectionProps {
  teamId: string
  permission: Record<string, any>
  playerTypes: any[]
  isInFrame: boolean
  isSoccer?: boolean
  sportName?: string
  sportId?: string
}

const PlayerSection = ({
  teamId,
  permission,
  playerTypes,
  isInFrame,
  sportName = 'hockey',
  sportId,
}: PlayerSectionProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])
  const {
    players,
    affiliates,
    selectedRosterId,
    shouldShowWaiver,
    requireEmail,
    refresh,
  } = useRosterContext()
  const { highlightedRows, addHighlightedRow } = useUIContext()
  const scrollToRow = useScrollToRow()

  const [sort, setSort] = useState('name_last')
  const [direction, setDirection] = useState('asc')
  // Edit Player modal (new full-screen Formik flow). Holds the roster row being
  // edited; null → closed.
  const [editPlayerRow, setEditPlayerRow] = useState<any>(null)
  // Family Management modal, reused as-is from the profile screen. Holds the
  // roster row whose family is being managed; null → closed.
  const [familyPlayerRow, setFamilyPlayerRow] = useState<any>(null)

  const handleSort = (field: string, order: string) => {
    setSort(field)
    setDirection(order)
  }

  const getOptions = useCallback(
    (item: any) => {
      if (!item) {
        return []
      }
      const slug = `/teams/${teamId}/rosters/${selectedRosterId}/players/${item.id}`

      const opts = [
        <MoreOptions
          key='more'
          // @ts-ignore
          options={[
            permission.update && {
              key: 'edit',
              // Opens the full-screen Edit Player modal prefilled from this row.
              title: capitalizeWords('Edit Profile'),
              iconName: 'pencil',
              onClick: () => setEditPlayerRow(item),
            },
            // Renders as Resend Invitation or Send Invitation depending on the
            // row's invite state. Hidden on exactly the rows InviteAction
            // renders nothing for (invited AND accepted), so no empty row.
            permission.update &&
              !(item.is_invited && item.is_accepted) && {
                key: 'invite',
                Component: InviteAction,
                componentProps: {
                  asMenuItem: true,
                  invite: {
                    is_invited: item.is_invited,
                    is_accepted: item.is_accepted,
                    invitation_id: item.invitation_id,
                  },
                  user: {
                    id: item.id,
                    name_first: item.name_first,
                    name_last: item.name_last,
                    player_type_id: item.player_type.id,
                    email: item?.email,
                  },
                  slug,
                  onComplete: async () => await refresh(),
                },
              },
            // Family Management is only meaningful once the player has
            // accepted their invitation — before that there's no user account
            // to attach family contacts to.
            permission.admin &&
              item.is_accepted && {
                key: 'family',
                title: capitalizeWords('Manage Family'),
                iconName: 'users',
                onClick: () => setFamilyPlayerRow(item),
              },
            permission.update && {
              key: 'remove',
              Component: OptionButtonDelete,
              componentProps: {
                buttonText: 'remove',
                slug,
                itemId: item.id,
                iconName: 'user-minus',
                title: `Remove ${item.name_first} ${item.name_last}`,
                type: t('common:player', {}),
                onComplete: refresh,
              },
            },
          ].filter(Boolean)}
        />,
      ]

      return opts.filter(Boolean)
    },
    [teamId, selectedRosterId, permission, refresh]
  )

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        title: t('common:name', {}),
        key: 'name',
        sorter: true,
        width: isMobile ? 200 : 300,
        ellipsis: true,
        showSorterTooltip: false,
        sortOrder:
          sort === 'name_last'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_: any, item: any) => {
          let to
          if (item?.id) {
            to = `${ROUTES.PLAYER_ROOT}/${item.id}`
          }
          return (
            <Link
              to={to || '#'}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                width: 100%;
                max-width: 268px;
                overflow: hidden;
                color: ${colors.NEUTRAL_0};
                &:hover {
                  color: ${colors.PRIMARY};
                }
              `}
            >
              <Avatar
                src={
                  getImageThumbnailId(item) ||
                  '/images/generic_placeholders/placeholder_player.png'
                }
                icon={<Icon name='user' fontSize={14} />}
                shape='square'
                css={css`
                  border: 1px solid #dbdbdb;
                  background-color: white;
                  padding: 2px;
                  flex-shrink: 0;
                  width: 32px;
                  height: 32px;
                `}
              />
              <div
                css={css`
                  flex: 1;
                  min-width: 0;
                  overflow: hidden;
                  display: flex;
                  flex-direction: column;
                `}
              >
                <Typography.Text
                  strong
                  ellipsis
                  css={css`
                    display: block;
                    width: 100%;
                    color: inherit;
                  `}
                >
                  {item?.name_first} {item?.name_last}
                </Typography.Text>
                {item?.customer_identifier && (
                  <Typography.Text
                    type='secondary'
                    ellipsis
                    css={css`
                      font-size: 12px;
                    `}
                  >
                    {item.customer_identifier}
                  </Typography.Text>
                )}
              </div>
            </Link>
          )
        },
      },
      {
        title: '#',
        dataIndex: 'player_number',
        key: 'player_number',
        sorter: true,
        showSorterTooltip: false,
        width: 60,
        sortOrder:
          sort === 'player_number'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
      },
      {
        // @ts-ignore
        title: t('common:position'),
        key: 'position',
        sorter: true,
        showSorterTooltip: false,
        width: 120,
        sortOrder:
          sort === 'player_type.id'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_: any, item: any) =>
          playerTypes?.find(
            (type: any) => type.id === parseInt(item?.player_type?.id, 10)
          )?.name_full || '-',
      },
      {
        title: 'DOB',
        key: 'dob',
        width: 120,
        render: (_: any, item: any) => {
          const dateAsString = item?.birth_date
            ? dayjs.utc(item.birth_date).format('YYYY-MM-DD')
            : ''
          if (dateAsString === 'Invalid Date') {
            return `${dateAsString} | ${item?.birth_date}`
          }
          return dateAsString
        },
      },
      {
        title: 'Status',
        key: 'status',
        width: 100,
        render: (_: any, item: any) => {
          const activeSuspensions =
            Array.isArray(item?.suspensions) &&
            item.suspensions.filter((s: any) => s.is_active) &&
            item.suspensions.length > 0

          if (activeSuspensions) {
            const suspension = item.suspensions.sort((a: any, b: any) => {
              const aStartDate = dayjs.utc(a.start_date)
              const bStartDate = dayjs.utc(b.start_date)
              return aStartDate.isBefore(bStartDate)
                ? -1
                : aStartDate.isAfter(bStartDate)
                ? 1
                : 0
            })[0]

            const startDate = suspension?.start_date
              ? dayjs.utc(suspension.start_date).format('MMM D')
              : ''
            const endDate = suspension?.end_date
              ? dayjs.utc(suspension.end_date).format('MMM D')
              : ''

            return (
              <div>
                <div
                  css={css`
                    text-transform: uppercase;
                    color: ${colors.FIERY_RED};
                    font-size: 14px;
                  `}
                >
                  Suspended
                </div>
                <div
                  css={css`
                    margin-top: 2px;
                    font-size: 13px;
                  `}
                >
                  {startDate} -{' '}
                  {suspension?.enforcement?.id !== 3 && endDate !== ''
                    ? endDate
                    : 'Indefinitely'}
                </div>
              </div>
            )
          } else if (item?.is_injured) {
            return 'Injured'
          }
          return null
        },
      },
    ]

    if (permission?.update) {
      cols.push({
        title: 'Email',
        key: 'email',
        width: 250,
        ellipsis: true,
        render: (_: any, item: any) => item?.email || '-',
      })
    }

    if (!isInFrame && shouldShowWaiver) {
      cols.push({
        title: 'Signed Waiver',
        key: 'waiver',
        width: 150,
        render: (_: any, item: any) => (item?.is_signed_waiver ? 'Yes' : 'No'),
      })
    }

    if (!isInFrame) {
      cols.push({
        title: 'Invite',
        key: 'invite',
        width: 100,
        render: (_: any, item: any) => {
          if (item?.is_accepted) {
            return 'Accepted'
          }
          if (item?.is_invited) {
            return 'Invited'
          }
          return ''
        },
      })
    }

    if (permission?.update || permission?.admin) {
      cols.push({
        title: '',
        key: 'options',
        align: 'right',
        width: 150,
        fixed: 'right',
        render: (_: any, item: any) => (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              justify-content: flex-end;
            `}
          >
            {getOptions(item)}
          </div>
        ),
      })
    }

    return cols
  }, [
    isMobile,
    sort,
    direction,
    playerTypes,
    isInFrame,
    shouldShowWaiver,
    permission,
    getOptions,
  ])

  const sorter = useCallback(
    (a: any, b: any) => {
      if (sort === 'name_last') {
        const aNameLast = a?.name_last?.toLowerCase() || ''
        const bNameLast = b?.name_last?.toLowerCase() || ''
        if (aNameLast < bNameLast) {
          return direction === 'asc' ? -1 : 1
        }
        if (aNameLast > bNameLast) {
          return direction === 'asc' ? 1 : -1
        }
        const aNameFirst = a?.name_first?.toLowerCase() || ''
        const bNameFirst = b?.name_first?.toLowerCase() || ''
        if (aNameFirst < bNameFirst) {
          return direction === 'asc' ? -1 : 1
        }
        if (aNameFirst > bNameFirst) {
          return direction === 'asc' ? 1 : -1
        }
        return 0
      }
      if (sort === 'player_number') {
        if (a?.player_number < b?.player_number) {
          return direction === 'asc' ? -1 : 1
        }
        if (a?.player_number > b?.player_number) {
          return direction === 'asc' ? 1 : -1
        }
      }
      if (sort === 'player_type.id') {
        if (a?.player_type?.id < b?.player_type?.id) {
          return direction === 'asc' ? -1 : 1
        }
        if (a?.player_type?.id > b?.player_type?.id) {
          return direction === 'asc' ? 1 : -1
        }
      }
      return 0
    },
    [sort, direction]
  )

  const sortedPlayers = useMemo(
    () => [...players].sort(sorter),
    [players, sorter]
  )
  const sortedAffiliates = useMemo(
    () => [...affiliates].sort(sorter),
    [affiliates, sorter]
  )

  if (sortedPlayers.length === 0 && sortedAffiliates.length === 0) {
    return (
      <div>
        <SectionTitle>Players</SectionTitle>
        <p
          css={css`
            color: ${colors.NEUTRAL_50};
            font-size: 14px;
          `}
        >
          No players have been added to this roster yet.
        </p>
      </div>
    )
  }

  const getRowClassName = (record: any) => {
    return highlightedRows.has(record.id) ? 'highlighted-row' : ''
  }

  const handleTableChange = (
    _pagination: any,
    _filters: any,
    tableSorter: any
  ) => {
    if (tableSorter.order) {
      const sortKey = tableSorter.columnKey || tableSorter.field
      const field =
        sortKey === 'name'
          ? 'name_last'
          : sortKey === 'position'
          ? 'player_type.id'
          : sortKey
      handleSort(field, tableSorter.order === 'ascend' ? 'asc' : 'desc')
    } else {
      handleSort('name_last', 'asc')
    }
  }

  return (
    <>
      <div
        css={css`
          width: 100%;
          max-width: 100%;
          overflow: hidden;

          @keyframes highlightFade {
            0% {
              box-shadow: inset 0 0 0 9999px ${colors.SUCCESS_300}4D;
            }
            60% {
              box-shadow: inset 0 0 0 9999px ${colors.SUCCESS_300}4D;
            }
            100% {
              box-shadow: inset 0 0 0 9999px transparent;
            }
          }
          .highlighted-row > td {
            animation: highlightFade 5s ease-out;
          }
        `}
      >
        <SectionTitle>Players</SectionTitle>
        {sortedPlayers.length === 0 && sortedAffiliates.length > 0 ? null : (
          <Table
            columns={columns}
            dataSource={sortedPlayers}
            rowKey='id'
            pagination={false}
            size='small'
            scroll={{ x: 'max-content' }}
            css={getTableStyles(isMobile)}
            rowClassName={getRowClassName}
            onChange={handleTableChange}
          />
        )}

        {sortedAffiliates.length > 0 && (
          <>
            <div
              css={css`
                margin-top: 24px;
                margin-bottom: 16px;
                font-weight: 700;
                color: ${colors.DEFAULT_FLAIR};
                text-transform: uppercase;
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <span>Spares</span>
              <Tooltip
                title='Spares are not full team members and cannot access attendance or team chat.'
                placement='top'
              >
                <span
                  css={css`
                    cursor: help;
                    display: flex;
                    align-items: center;
                    margin-bottom: 2px;
                  `}
                >
                  <QuestionMarkIcon width={20} height={20} />
                </span>
              </Tooltip>
            </div>
            <Table
              columns={columns}
              dataSource={sortedAffiliates}
              rowKey='id'
              pagination={false}
              size='small'
              scroll={{ x: 'max-content' }}
              css={getTableStyles(isMobile)}
              rowClassName={getRowClassName}
              onChange={handleTableChange}
            />
          </>
        )}
      </div>
      {selectedRosterId ? (
        <RosterPlayerModal
          isOpen={Boolean(editPlayerRow)}
          editPlayer={editPlayerRow || undefined}
          onClose={() => setEditPlayerRow(null)}
          onSuccess={async () => {
            await refresh()
            if (editPlayerRow?.id) {
              addHighlightedRow(editPlayerRow.id, 'success')
              scrollToRow(editPlayerRow.id)
            }
            setEditPlayerRow(null)
          }}
          teamId={teamId}
          rosterId={selectedRosterId}
          sportId={sportId ?? null}
          sportName={sportName}
          hasAdminPermission={permission?.admin}
          requireEmail={requireEmail}
        />
      ) : null}
      {/* Only reachable from the admin-gated menu item, so canManage is always
          true here — the invite/edit/remove actions are the point of the entry.
          The roster scope routes every call through the team-admin endpoints
          instead of the self-scoped ones, and passing the row as `player`
          means the contact modals render their card from data we already hold
          rather than re-fetching a player this caller doesn't own. */}
      {selectedRosterId ? (
        <FamilyManagementModal
          isOpen={Boolean(familyPlayerRow)}
          playerId={familyPlayerRow?.id ?? null}
          onClose={() => setFamilyPlayerRow(null)}
          canManage
          scope={{ kind: 'roster', teamId, rosterId: selectedRosterId }}
          player={familyPlayerRow}
        />
      ) : null}
    </>
  )
}

const mapStateToProps = (state: any, { sportId }: any) => {
  return {
    playerTypes: getPlayerTypesBySportId(state, sportId),
    isInFrame: state.auth.inFrame,
  }
}

export default connect(mapStateToProps)(PlayerSection)
